// This is the primary entrypoint for mounting the Preact bundle.
// It is loaded via a <script> tag from our generated snippets and can always
// expect to have a div#__3PLAY_PRIVATE_ACCESS_PLAYER__ to mount onto.
//
// It is also pulled in from the iframe version of the snippet (index.html).

import { h, render } from "preact";
import "preact/devtools"; // just want the devtools bridge (~240B)
import { useState, useEffect } from "preact/hooks";
import RecordedContainer from "./ui/RecordedContainer";
import { SettingsProvider } from "./ui/SettingsContext";
import { ErrorBoundary } from "./ui/shared/components";
import { ParsedSettings, WindowSettings } from "./types/settings";
import getFontPreference from "./ui/helpers/getFontPreference";
import { fetchData } from "./ui/shared";
import { RootDataType } from "./types/rootDataTypes";

import {
  defaultSettings,
  DEFAULT_SHOW_AD,
  DEFAULT_SHOW_DOWNLOAD,
  DEFAULT_SHOW_LANGUAGE_DROPDOWN,
  DEFAULT_SHOW_TRANSCRIPT,
} from "./constants/defaults";

import { recordedEndpoint } from "./ui/helpers/routes";

import { parseQueryBoolean } from "./snippets/util";
import { Loading } from "./ui/shared/components/Loading";

import styles from "bundle-text:./ui/index.css";

const shadowDomAvailable = "attachShadow" in document.head;

declare global {
  interface Window {
    __3PLAY_ACCESS_PLAYER_SETTINGS__: WindowSettings;
    __PRIVATE_REMOUNT_THREEPLAY_ACCESS_PLAYER: () => void;
    __3PLAY_DEBUG_AUDIO_DESCRIPTION__?: (enable: boolean) => void;
  }
}

function initializeApp() {
  const pluginEl =
    document.querySelector(
      '[data-threeplay-access-player="1"] [data-mount="1"]'
    ) ??
    (document.getElementById("__3PLAY_PRIVATE_ACCESS_PLAYER__") as Element);

  let appRoot = pluginEl;

  if (shadowDomAvailable) {
    const shadow = pluginEl.attachShadow({ mode: "open" });
    appRoot = document.createElement("div");
    appRoot.id = "shadow-root";

    shadow.appendChild(appRoot);
  }

  /**
   * get parent site font to see if on of plugins
   * available fonts should be preferred
   */
  const fontPreference = getFontPreference(
    getComputedStyle(appRoot).getPropertyValue("font-family")
  );

  function getDataAttributes(): WindowSettings | null {
    const element = document.querySelector(
      '[data-threeplay-access-player="1"]'
    ) as HTMLElement;

    if (!element) {
      return null;
    }

    // TODO typeguard
    return { ...element.dataset } as WindowSettings;
  }

  const parsedAspect = Number(window.__3PLAY_ACCESS_PLAYER_SETTINGS__?.aspect);

  function getWindowSettings(): WindowSettings {
    return {
      ...window.__3PLAY_ACCESS_PLAYER_SETTINGS__,
      inIframe: Boolean(window.__3PLAY_ACCESS_PLAYER_SETTINGS__.inIframe),
      aspect: Number.isNaN(parsedAspect) ? undefined : parsedAspect,
    };
  }

  const Plugin = () => {
    // TODO: render failure state with lookup params.
    const windowSettings = getDataAttributes() ?? getWindowSettings();
    const settings: ParsedSettings = {
      ...defaultSettings,
      ...windowSettings,
      hideTitle: parseQueryBoolean(windowSettings.hideTitle),
      inIframe: parseQueryBoolean(windowSettings.inIframe),
      showTranscript: parseQueryBoolean(
        windowSettings.showTranscript,
        DEFAULT_SHOW_TRANSCRIPT
      ),
      showDownload: parseQueryBoolean(
        windowSettings.showDownload,
        DEFAULT_SHOW_DOWNLOAD
      ),
      showLanguageDropdown: parseQueryBoolean(
        windowSettings.showLanguageDropdown,
        DEFAULT_SHOW_LANGUAGE_DROPDOWN
      ),
      showAd: parseQueryBoolean(windowSettings.showAd, DEFAULT_SHOW_AD),
    };

    const [rootData, setRootData] = useState<RootDataType>();
    const [failed, setFailed] = useState(false);

    type RootResponse = {
      data: RootDataType;
    };

    const getRootData = async (url: URL) => {
      setFailed(false);

      const { data, error } = await fetchData<RootResponse>(url);
      if (!error && data) {
        setRootData(data.data);
      }

      setFailed(Boolean(error));
    };

    useEffect(() => {
      getRootData(recordedEndpoint(settings, "/"));
    }, []);

    /**
     * generate plugin aria label based on features available
     */
    let ariaLabel;
    if (rootData) {
      const adAvailable =
        settings.showAd &&
        Object.keys(rootData.available_audio_description_languages).length > 0;

      const transcriptAvailable =
        settings.showTranscript &&
        Object.keys(rootData.available_transcript_languages).length > 0;

      const playAs = "[audio/video]"; // TODO: add media type to root data
      const title = rootData.name;

      ariaLabel = `${
        adAvailable && transcriptAvailable
          ? `Audio description and Transcript are`
          : adAvailable && !transcriptAvailable
          ? `Audio description is`
          : !adAvailable && transcriptAvailable
          ? `Transcript is`
          : `No Audio Description or Transcript are`
      } available for the following ${playAs} file: ${title}`;
    }

    return (
      <SettingsProvider appRoot={pluginEl} initialSettings={settings}>
        {/*
          We must inline styles when using the Shadow DOM. This approach is
          generally incompatible with CSS Modules, so we go old-school with
          BEM-like class names and bundled imports. See index.css.
        */}
        <style>{styles}</style>
        <div
          class={`plugin ${settings.inIframe ? "" : "shadow"}`}
          style={`font-family: ${fontPreference}`}
        >
          <section
            aria-label={ariaLabel}
            data-testid="plugin-container"
            data-layout={settings.layout}
            class="section"
          >
            <ErrorBoundary>
              {failed ? (
                <div class="loadingContainer">
                  <p>
                    Failed to load the Access Player.{" "}
                    <a
                      class="a"
                      onClick={() =>
                        getRootData(recordedEndpoint(settings, "/"))
                      }
                    >
                      Try again?
                    </a>
                  </p>
                </div>
              ) : rootData ? (
                <RecordedContainer {...rootData} />
              ) : (
                <div class="loadingContainer">
                  <Loading />
                </div>
              )}
            </ErrorBoundary>
          </section>
        </div>
      </SettingsProvider>
    );
  };

  render(<Plugin />, appRoot);
}

initializeApp();

// This is a backdoor way of remounting the component in exceptional
// circumstances. Namely, the Access Player Builder triggers remounts
// based on settings changes and this allows it to re-render even when this file
// has already been run (missing the side-effect of mounting the first time).
window.__PRIVATE_REMOUNT_THREEPLAY_ACCESS_PLAYER = initializeApp;
